import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';

import maplibregl from 'maplibre-gl';

import { GetTripRes_stops } from '../../global-states/trip';
import SocketHandler from '../../socket';
import { ReducerState } from '../../socket/reducer';
import {
  alphabet,
  getStopVisualStates,
  stopBadgeClass,
  StopVisualState,
} from '../../utils/utils';
import {
  createBusMarkerElement,
  setBusMarkerHeading,
  setBusMarkerStale,
  STALE_OPACITY,
} from './bus-marker';
import {
  bearingDeg,
  distanceM,
  lerpHeading,
  MOVING_THRESHOLD_M,
  pathLengthM,
  planMotion,
  pointAtDistance,
  sliceByDistance,
  LngLat,
  MotionMode,
} from './bus-motion';
import MapLegend from './map-legend';
import {
  encodedPathsToFeatureCollection,
  encodedPathsToLngLats,
  LineFeatureCollection,
  LIVE_PATH_COLOR,
  PLANNED_PATH_COLOR,
  protomapsStyle,
} from './map-style';

import 'maplibre-gl/dist/maplibre-gl.css';

type MapLibreMap = InstanceType<typeof maplibregl.Map>;
type MapLibreMarker = InstanceType<typeof maplibregl.Marker>;
type MapLibrePopup = InstanceType<typeof maplibregl.Popup>;
type MapLibreGeoJSONSource = InstanceType<typeof maplibregl.GeoJSONSource>;

type Props = {
  data?: GetTripRes_stops[];
  children?: any;
  stops?: GetTripRes_stops[];
  socketState: ReducerState;
  socketDispatch: React.Dispatch<any>;
  staleAgeMs?: number;
};

const SG_CENTER: [number, number] = [103.8198, 1.3521];

// Fallback frame when a trip has no stop coordinates (mirrors the previous
// Google Maps LatLngBounds over Singapore).
const SG_FALLBACK_BOUNDS: [[number, number], [number, number]] = [
  [103.636559, 1.341102],
  [104.030988, 1.355152],
];

const TRIP_PATH_SOURCE = 'trip-path';
const TRIP_PATH_LAYER = 'trip-path-layer';
const SOCKET_PATH_SOURCE = 'socket-path';
const SOCKET_PATH_LAYER = 'socket-path-layer';

// Stop markers mirror the sheet timeline's lettered badges (A, B, C…) and
// palette: first blue, last red (pulsing halo), in-between gray, passed
// green. Same class strings as position-card so Tailwind generates them.
const STOP_BADGE_SIZE = 26;

const applyStopBadge = (
  el: HTMLDivElement,
  letter: string,
  state: StopVisualState
) => {
  while (el.firstChild) el.removeChild(el.firstChild);
  if (state === 'last') {
    const halo = document.createElement('div');
    halo.className = 'absolute inset-[-3px] animate-ro-pulse rounded-full bg-ro-red opacity-40';
    el.appendChild(halo);
  }
  const badge = document.createElement('div');
  badge.className = `absolute inset-0 flex items-center justify-center rounded-full text-xs font-extrabold ${stopBadgeClass(
    state
  )}`;
  badge.textContent = letter;
  el.appendChild(badge);
};

const createStopBadgeElement = (
  letter: string,
  state: StopVisualState
): HTMLDivElement => {
  const el = document.createElement('div');
  el.className = 'relative h-[26px] w-[26px] cursor-pointer';
  applyStopBadge(el, letter, state);
  return el;
};

const stopLetter = (stop: GetTripRes_stops): string => (
  alphabet[Number(stop?.stop_index)] ?? ''
);

// The map canvas runs under the whole viewport, but the lg+ layout covers its
// left 412px with the sidebar and the mobile layout covers the bottom with
// the sheet and the top with the header overlay. Auto-fit must pad those
// regions out or the framed route hides behind the UI.
const PANEL_WIDTH = 412;

export const resolveFitPadding = (
  width: number,
  height: number,
  isDesktop: boolean
) => {
  const raw = isDesktop
    ? { top: 60, bottom: 60, left: PANEL_WIDTH + 48, right: 48 }
    : { top: 110, bottom: Math.floor(height * 0.42), left: 28, right: 28 };
  // MapLibre rejects padding larger than the canvas — cap each axis at 85%.
  const hScale = raw.left + raw.right > width * 0.85
    ? (width * 0.85) / (raw.left + raw.right)
    : 1;
  const vScale = raw.top + raw.bottom > height * 0.85
    ? (height * 0.85) / (raw.top + raw.bottom)
    : 1;
  return {
    top: Math.floor(raw.top * vScale),
    bottom: Math.floor(raw.bottom * vScale),
    left: Math.floor(raw.left * hScale),
    right: Math.floor(raw.right * hScale),
  };
};

// Popup content mirrors the previous InfoWindow markup: bold description,
// name, then coordinates to five decimal places. Built with DOM calls so
// dynamic values are written as text, not interpolated into HTML.
const createPopupContent = (stop: GetTripRes_stops): HTMLDivElement => {
  const root = document.createElement('div');
  root.className = 'p-2 pr-5 pb-3';

  const header = document.createElement('div');
  header.className = 'mb-2';
  const title = document.createElement('p');
  title.className = 'pb-1';
  const strong = document.createElement('strong');
  strong.textContent = stop?.description ?? '';
  title.appendChild(strong);
  const name = document.createElement('p');
  name.textContent = stop?.name ?? '';
  header.appendChild(title);
  header.appendChild(name);

  const coords = document.createElement('p');
  const em = document.createElement('em');
  em.className = 'font-thin';
  em.textContent = `(${Number(stop?.latitude).toFixed(5)}, ${Number(
    stop?.longitude
  ).toFixed(5)})`;
  coords.appendChild(em);

  root.appendChild(header);
  root.appendChild(coords);
  return root;
};

const upsertLineLayer = (
  map: MapLibreMap,
  sourceId: string,
  layerId: string,
  color: string,
  data: LineFeatureCollection
) => {
  const source = map.getSource(sourceId) as MapLibreGeoJSONSource | undefined;
  if (source) {
    source.setData(data);
    return;
  }
  map.addSource(sourceId, { type: 'geojson', data });
  map.addLayer({
    id: layerId,
    type: 'line',
    source: sourceId,
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint: {
      'line-color': color,
      'line-width': 4,
      'line-opacity': 1,
    },
  });
};

const Gmap = ({
  data,
  children,
  stops,
  socketState,
  socketDispatch,
  staleAgeMs,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<MapLibreMap | null>(null);
  const stopMarkersRef = useRef<
  { marker: MapLibreMarker; el: HTMLDivElement; stop: GetTripRes_stops }[]
  >([]);
  const busMarkersRef = useRef<Map<string, MapLibreMarker>>(new Map());
  const popupRef = useRef<MapLibrePopup | null>(null);
  const [mapReady, setMapReady] = useState(false);

  type BusAnim = {
    el: HTMLDivElement;
    path: LngLat[];
    pathLenM: number;
    startTs: number;
    durationMs: number;
    fromHeading: number;
    toHeading: number;
    mode: MotionMode;
    lastPoint: LngLat;
    lastHeading: number;
  };

  const busAnimRef = useRef<Map<string, BusAnim>>(new Map());
  const lastFixAtRef = useRef<Map<string, number>>(new Map());
  const rafRef = useRef<number | null>(null);

  // The live socket path per vehicle, decoded once per fix. It begins at the
  // newly reported position — which is ahead of where the marker currently is
  // while an animation is in flight, hence the head-stitching in paintLivePath.
  const livePathRef = useRef<Map<string, LngLat[]>>(new Map());

  // Decoded once per trip, not per fix — decoding on every socket tick would
  // re-parse the whole route several times a second.
  const routeRef = useRef<LngLat[]>([]);
  useEffect(() => {
    routeRef.current = encodedPathsToLngLats((stops ?? []).map((s) => s?.path));
  }, [stops]);

  const { busList } = socketState;
  // Latest bus list for effects that must not re-run on every socket tick.
  const busListRef = useRef(busList);
  busListRef.current = busList;

  // Create/destroy the map once per component lifetime.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const map = new maplibregl.Map({
      container,
      style: protomapsStyle('light'),
      center: SG_CENTER,
      zoom: 10,
    });
    // Bottom-right keeps the zoom buttons clear of the LIVE pill and chat
    // button that overlay the map's top-right corner.
    map.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      'bottom-right'
    );
    map.on('load', () => setMapReady(true));
    mapInstanceRef.current = map;

    // The Map instance in busMarkersRef is never reassigned, so capturing it
    // here keeps the cleanup reading live marker state.
    const busMarkers = busMarkersRef.current;

    return () => {
      stopMarkersRef.current.forEach(({ marker }) => marker.remove());
      stopMarkersRef.current = [];
      busMarkers.forEach((marker) => marker.remove());
      busMarkers.clear();
      popupRef.current?.remove();
      popupRef.current = null;
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  // Stop markers (pickup / dropoff / intermediate) + tap-for-info popups,
  // and bounds fitting over stop coordinates with the Singapore fallback.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady) return;

    stopMarkersRef.current.forEach(({ marker }) => marker.remove());
    stopMarkersRef.current = [];
    popupRef.current?.remove();
    popupRef.current = null;

    const bounds = new maplibregl.LngLatBounds();
    const states = getStopVisualStates(data ?? [], busListRef.current?.[0]);

    (data ?? []).forEach((stop, index) => {
      const { latitude, longitude } = stop;
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

      const el = createStopBadgeElement(
        stopLetter(stop),
        states[index] ?? 'middle'
      );
      el.addEventListener('click', (event) => {
        event.stopPropagation();
        popupRef.current?.remove();
        popupRef.current = new maplibregl.Popup({
          anchor: 'bottom',
          offset: [0, -(STOP_BADGE_SIZE / 2 + 6)],
        })
          .setLngLat([longitude, latitude])
          .setDOMContent(createPopupContent(stop))
          .addTo(map);
      });

      const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([longitude, latitude])
        .addTo(map);
      stopMarkersRef.current.push({ marker, el, stop });
      bounds.extend([longitude, latitude]);
    });

    const el = containerRef.current;
    const padding = resolveFitPadding(
      el?.clientWidth || window.innerWidth,
      el?.clientHeight || window.innerHeight,
      window.matchMedia('(min-width: 1024px)').matches
    );
    if (bounds.isEmpty()) {
      map.fitBounds(SG_FALLBACK_BOUNDS, { padding });
    } else {
      map.fitBounds(bounds, { padding });
    }
  }, [data, mapReady]);

  // Trip path polylines from trip stops.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady) return;
    upsertLineLayer(
      map,
      TRIP_PATH_SOURCE,
      TRIP_PATH_LAYER,
      PLANNED_PATH_COLOR,
      encodedPathsToFeatureCollection((stops ?? []).map((stop) => stop?.path))
    );
  }, [stops, mapReady]);

  // The blue live line has to stay attached to the marker. `stopsList[].path`
  // begins at the newest fix, so drawing it raw makes the route jump ahead the
  // instant a fix lands and leaves the bus trailing behind its own path for the
  // whole animation — it reads as the bus lagging. Stitching the untraversed
  // remainder of the current animation segment onto the front keeps the bus at
  // the line's head, and the line retracts beneath it as it drives.
  const paintLivePath = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const features: LineFeatureCollection['features'] = [];

    livePathRef.current.forEach((livePath, key) => {
      const anim = busAnimRef.current.get(key);
      let head: LngLat[] = [];

      if (anim && anim.pathLenM > 0) {
        const t = anim.durationMs > 0
          ? Math.min(1, (performance.now() - anim.startTs) / anim.durationMs)
          : 1;
        if (t < 1) head = sliceByDistance(anim.path, anim.pathLenM * t, anim.pathLenM);
      }

      let coordinates: LngLat[] = livePath;
      if (head.length) {
        // head ends at the fix that livePath starts from — drop the repeat so
        // the joined line carries no zero-length segment.
        const tail = head[head.length - 1]!;
        const first = livePath[0];
        const rest = first && tail[0] === first[0] && tail[1] === first[1]
          ? livePath.slice(1)
          : livePath;
        coordinates = [...head, ...rest];
      }

      if (coordinates.length < 2) return;
      features.push({
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates },
      });
    });

    upsertLineLayer(
      map,
      SOCKET_PATH_SOURCE,
      SOCKET_PATH_LAYER,
      LIVE_PATH_COLOR,
      { type: 'FeatureCollection', features },
    );
  }, []);

  // One loop drives every vehicle. It parks itself when nothing is moving, so
  // an idle map costs no frames.
  const stepAnimations = useCallback(() => {
    const now = performance.now();
    let stillMoving = false;

    busAnimRef.current.forEach((anim, key) => {
      const marker = busMarkersRef.current.get(key);
      if (!marker) return;

      const t = anim.durationMs > 0
        ? Math.min(1, (now - anim.startTs) / anim.durationMs)
        : 1;

      const { point, bearing } = pointAtDistance(anim.path, anim.pathLenM * t);

      // On a route the tangent IS the true heading and carries the bus through
      // corners; a reported heading is a point sample that cannot express one.
      const heading = anim.mode === 'route' && anim.pathLenM > MOVING_THRESHOLD_M
        ? bearing
        : lerpHeading(anim.fromHeading, anim.toHeading, t);

      marker.setLngLat(point);
      setBusMarkerHeading(anim.el, heading);
      // Write the visible position back into the map entry (not the `anim`
      // parameter itself — airbnb's no-param-reassign, props:true) so the
      // next fix can continue from where the bus actually is on screen.
      busAnimRef.current.set(key, { ...anim, lastPoint: point, lastHeading: heading });

      if (t < 1) stillMoving = true;
    });

    // Repaint in the same frame the marker moved, so the line's head and the
    // bus never disagree by even one frame.
    paintLivePath();

    rafRef.current = stillMoving ? requestAnimationFrame(stepAnimations) : null;
  }, [paintLivePath]);

  // Backgrounded tabs pause rAF; cancel on unmount so a queued frame never
  // touches a removed marker.
  useEffect(() => () => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  // Live socket data: bus markers (position + heading) keyed by vehicle,
  // and the socket-projected path polylines.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady) return;

    const markers = busMarkersRef.current;
    const seen = new Set<string>();

    (busList ?? []).forEach((bus, index) => {
      const latitude = bus?.driverInfo?.latitude;
      const longitude = bus?.driverInfo?.longitude;
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

      const key = bus?.vehicleId ?? `bus-${index}`;
      seen.add(key);

      const toPoint: LngLat = [longitude as number, latitude as number];
      const reported = bus?.driverInfo?.heading;
      const nowMs = Date.now();
      const lastAt = lastFixAtRef.current.get(key);
      lastFixAtRef.current.set(key, nowMs);

      const existing = markers.get(key);
      if (!existing) {
        const heading = Number.isFinite(reported) ? (reported as number) : 0;
        const el = createBusMarkerElement(heading);
        const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat(toPoint)
          .addTo(map);
        markers.set(key, marker);
        busAnimRef.current.set(key, {
          el,
          path: [toPoint, toPoint],
          pathLenM: 0,
          startTs: performance.now(),
          durationMs: 0,
          fromHeading: heading,
          toHeading: heading,
          mode: 'snap',
          lastPoint: toPoint,
          lastHeading: heading,
        });
        return;
      }

      const anim = busAnimRef.current.get(key);
      // Continue from where the bus VISIBLY is, not from the previous fix —
      // otherwise a fix arriving mid-animation snaps backwards first.
      const fromPoint = anim?.lastPoint ?? toPoint;
      const fromHeading = anim?.lastHeading ?? 0;

      const plan = planMotion({
        fromPoint,
        toPoint,
        route: routeRef.current,
        isDetour: !!bus?.isDetour,
        elapsedSinceLastFixMs: lastAt ? nowMs - lastAt : 0,
      });

      // Below the movement threshold the from->to vector is pure GPS noise,
      // and so is a heading a stationary receiver reports. Letting either
      // through spins a parked bus on the spot — 0.6m of jitter was measured
      // swinging the marker 13.8deg -> 61.5deg -> 104.6deg. Hold the last
      // real bearing instead, in every mode.
      const movedM = distanceM(fromPoint, toPoint);
      const reportedHeading = Number.isFinite(reported)
        ? (reported as number)
        : bearingDeg(fromPoint, toPoint);
      const toHeading = movedM <= MOVING_THRESHOLD_M ? fromHeading : reportedHeading;

      if (plan.mode === 'snap') {
        existing.setLngLat(toPoint);
        setBusMarkerHeading(anim?.el ?? (existing.getElement() as HTMLDivElement), toHeading);
      }

      // Seed from where the FIRST FRAME will actually render, which is
      // plan.path[0]. In route mode that is the projection of fromPoint onto
      // the route — up to OFF_ROUTE_THRESHOLD_M (80m) away from fromPoint
      // itself — so seeding fromPoint would leave lastPoint disagreeing with
      // what is on screen. (For straight it is fromPoint and for snap it is
      // toPoint, so this is the same value those modes had.)
      const seedPoint: LngLat = plan.path[0] ?? toPoint;

      busAnimRef.current.set(key, {
        el: anim?.el ?? (existing.getElement() as HTMLDivElement),
        path: plan.path,
        pathLenM: pathLengthM(plan.path),
        startTs: performance.now(),
        durationMs: plan.durationMs,
        fromHeading,
        toHeading,
        mode: plan.mode,
        lastPoint: seedPoint,
        lastHeading: plan.mode === 'snap' ? toHeading : fromHeading,
      });
    });

    if (rafRef.current === null) {
      rafRef.current = requestAnimationFrame(stepAnimations);
    }

    markers.forEach((marker, key) => {
      if (!seen.has(key)) {
        marker.remove();
        markers.delete(key);
        busAnimRef.current.delete(key);
        lastFixAtRef.current.delete(key);
      }
    });

    // Decode each vehicle's live path once per fix — paintLivePath runs every
    // frame and must not re-parse polylines at 60fps.
    const livePaths = livePathRef.current;
    livePaths.clear();
    (busList ?? []).forEach((bus, index) => {
      const key = bus?.vehicleId ?? `bus-${index}`;
      livePaths.set(
        key,
        encodedPathsToLngLats((bus?.stopsList ?? []).map((stop) => stop?.path)),
      );
    });
    paintLivePath();

    // Live badge states: repaint stop markers in place (marker identity and
    // any open popup survive) as ETAs arrive and stops get passed.
    const entries = stopMarkersRef.current;
    if (entries.length) {
      const states = getStopVisualStates(
        entries.map((entry) => entry.stop),
        busList?.[0]
      );
      entries.forEach((entry, index) => {
        applyStopBadge(
          entry.el,
          stopLetter(entry.stop),
          states[index] ?? 'middle'
        );
      });
    }
  }, [busList, mapReady, stepAnimations, paintLivePath]);

  // Keep every rendered marker's staleness in sync as staleAgeMs ticks,
  // without re-running the busList effect above on every tick.
  //
  // The fade goes through MapLibre's setOpacity, not the element's style:
  // Marker._update() is bound to the map's `move` event and also runs on every
  // setLngLat, and it ends by writing the marker's own _opacity onto the
  // element. Setting element opacity directly means a parent who pans the map
  // to find the bus sees a fully opaque, normal-looking marker — and the fade
  // is the only stale signal on the map itself.
  // mapReady is a dependency because the markers this walks do not exist
  // until the busList effect above has run, and that one is gated on it.
  useEffect(() => {
    const opacity = staleAgeMs === undefined ? '1' : String(STALE_OPACITY);
    busAnimRef.current.forEach((anim, key) => {
      setBusMarkerStale(anim.el, staleAgeMs);
      busMarkersRef.current.get(key)?.setOpacity(opacity);
    });
  }, [staleAgeMs, busList, mapReady]);

  return (
    <div className="h-full w-full">
      <div ref={containerRef} className="h-full w-full" />
      <MapLegend />
      {children}
      <SocketHandler
        socketState={socketState}
        socketDispatch={socketDispatch}
      />
    </div>
  );
};

export default Gmap;
